'use client';

import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import appLogger from '@/utils/logger';
import { refreshToken as refreshTokenApi } from '@/services/auth';

// Never log tokens or refresh tokens; only log operation outcomes for audit.

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  role: string;
  status: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, user: User, refreshToken?: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Schedule proactive token refresh (5 minutes before expiry)
  const scheduleTokenRefresh = useCallback((expiryTime: number) => {
    // Clear existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    
    const now = Date.now();
    const timeUntilExpiry = expiryTime - now;
    const refreshTime = timeUntilExpiry - (5 * 60 * 1000); // 5 minutes before expiry
    
    if (refreshTime > 0) {
      refreshTimerRef.current = setTimeout(async () => {
        const storedRefreshToken = localStorage.getItem('refreshToken');
        if (storedRefreshToken) {
          // Use refreshTokenApi directly to avoid circular dependency
          try {
            const refreshResult = await refreshTokenApi(storedRefreshToken);
            if (refreshResult.success && refreshResult.accessToken) {
              localStorage.setItem('token', refreshResult.accessToken);
              localStorage.setItem('accessToken', refreshResult.accessToken);
              if (refreshResult.refreshToken) {
                localStorage.setItem('refreshToken', refreshResult.refreshToken);
              }
              const newExpiry = Date.now() + (24 * 60 * 60 * 1000);
              localStorage.setItem('tokenExpiry', newExpiry.toString());
              setToken(refreshResult.accessToken);
              scheduleTokenRefresh(newExpiry);
            }
          } catch {
            // Silent: will retry on next request or user re-auth
          }
        }
      }, refreshTime);
    }
  }, []);

  // Function to attempt token refresh
  const attemptTokenRefresh = useCallback(async (storedRefreshToken: string): Promise<boolean> => {
    try {
      const refreshResult = await refreshTokenApi(storedRefreshToken);
      if (refreshResult.success && refreshResult.accessToken) {
        localStorage.setItem('token', refreshResult.accessToken);
        localStorage.setItem('accessToken', refreshResult.accessToken);
        if (refreshResult.refreshToken) {
          localStorage.setItem('refreshToken', refreshResult.refreshToken);
        }
        const newExpiry = Date.now() + (24 * 60 * 60 * 1000);
        localStorage.setItem('tokenExpiry', newExpiry.toString());
        setToken(refreshResult.accessToken);
        scheduleTokenRefresh(newExpiry);
        return true;
      }
      return false;
    } catch (error) {
      appLogger.errorWithContext(
        error instanceof Error ? error : new Error('Token refresh failed'),
        'AuthContext.attemptTokenRefresh',
        {}
      );
      return false;
    }
  }, [scheduleTokenRefresh]);

  useEffect(() => {
    // Check for existing token and user data on app load
    // Only run once on mount, not on every dependency change
    let isMounted = true;
    
    const checkAuth = async () => {
      // Only run on client side
      if (typeof window === 'undefined') {
        setIsLoading(false);
        return;
      }
      
      try {
        const storedToken = localStorage.getItem('token') || localStorage.getItem('accessToken');
        const storedUser = localStorage.getItem('user');
        const storedRefreshToken = localStorage.getItem('refreshToken');
        const tokenExpiry = localStorage.getItem('tokenExpiry');

        if (storedToken && storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            const expiryTime = tokenExpiry ? parseInt(tokenExpiry, 10) : null;
            
            // If no expiry time exists, set one (24 hours from now) to prevent immediate expiry
            if (!expiryTime) {
              const newExpiry = Date.now() + (24 * 60 * 60 * 1000);
              localStorage.setItem('tokenExpiry', newExpiry.toString());
              if (isMounted) {
                setToken(storedToken);
                setUser(parsedUser);
                scheduleTokenRefresh(newExpiry);
              }
              setIsLoading(false);
              return;
            }
            
            if (Date.now() >= expiryTime) {
              if (storedRefreshToken) {
                const refreshSuccess = await attemptTokenRefresh(storedRefreshToken);
                if (refreshSuccess && isMounted) {
                  setUser(parsedUser);
                  setIsLoading(false);
                  return;
                }
              }
              if (isMounted) {
                localStorage.removeItem('token');
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('user');
                localStorage.removeItem('tokenExpiry');
                setToken(null);
                setUser(null);
              }
            } else {
              // Token is valid
              if (isMounted) {
                setToken(storedToken);
                setUser(parsedUser);
                
                // Schedule proactive refresh if expiry time exists
                scheduleTokenRefresh(expiryTime);
              }
            }
            // Set loading to false after processing token
            if (isMounted) {
              setIsLoading(false);
            }
          } catch (parseError) {
            // Error parsing user data - might be corrupted
            appLogger.errorWithContext(
              parseError instanceof Error ? parseError : new Error('Error parsing user data'),
              'AuthContext.checkAuth.parseUser',
              {}
            );
            // Don't clear everything on parse error - might be temporary
            // Just log and continue without user data
            if (isMounted && storedToken) {
              setToken(storedToken);
            }
          }
        } else {
          // No token or user found (e.g. first visit) — no log needed
        }
      } catch (error) {
        appLogger.errorWithContext(
          error instanceof Error ? error : new Error('Error checking authentication'),
          'AuthContext.checkAuth',
          {}
        );
        // Only clear on actual errors, not on missing data
        // Don't clear localStorage on every error - might be temporary
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    checkAuth();
    
    // Cleanup timer on unmount
    return () => {
      isMounted = false;
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const login = async (newToken: string, userData: User, refreshTokenValue?: string) => {
    try {
      // Only run on client side
      if (typeof window === 'undefined') {
        return;
      }
      
      // Set token expiry to 24 hours from now
      const tokenExpiry = Date.now() + (24 * 60 * 60 * 1000);
      
      // Store in localStorage (both keys for compatibility)
      localStorage.setItem('token', newToken);
      localStorage.setItem('accessToken', newToken);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('tokenExpiry', tokenExpiry.toString());
      
      // Store refresh token if provided
      if (refreshTokenValue) {
        localStorage.setItem('refreshToken', refreshTokenValue);
      }
      
      // Update state with provided user first (fast UI)
      setToken(newToken);
      setUser(userData);
      
      // Schedule proactive token refresh (5 minutes before expiry)
      scheduleTokenRefresh(tokenExpiry);

      // Skip auto-refresh from /auth/me to avoid overriding employee designation
    } catch (error) {
      appLogger.errorWithContext(
        error instanceof Error ? error : new Error('Error during login'),
        'AuthContext.login',
        {}
      );
      throw error;
    }
  };

  const logout = () => {
    try {
      // Only run on client side
      if (typeof window === 'undefined') {
        return;
      }
      
      // Clear refresh timer
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      
      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('tokenExpiry');
      
      // Clear state
      setToken(null);
      setUser(null);
    } catch (error) {
      appLogger.errorWithContext(
        error instanceof Error ? error : new Error('Error during logout'),
        'AuthContext.logout',
        {}
      );
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isLoading,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
